import { DocumentNode } from 'graphql';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Box, Button, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Modal } from '@app/ui/modal';
import { Alert } from '@app/ui/alert';
import { error as toastError, success as toastSuccess } from '@app/snackbars';

import { getGraphQLErrorMessage } from '../../utils';

export interface ColorItemDeleteDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly mutation: DocumentNode;
  readonly id: number;
  readonly itemText: string;
  readonly dialogTitle: string;
  readonly successMessage: string;
  readonly onDeleted: () => Promise<unknown> | void;
}

// One small dialog shared by a colour scoring rule and a colour gate delete (both are
// `<mutation>(input: {id})`-shaped, draft-only, mirroring `RuleDeleteDialog`'s watchlist shape).
export const ColorItemDeleteDialog = ({
  open,
  onClose,
  mutation,
  id,
  itemText,
  dialogTitle,
  successMessage,
  onDeleted,
}: ColorItemDeleteDialogProps) => {
  const { t } = useTranslation('common');

  const [deleteItem, { loading: deleting }] = useMutation<Record<string, boolean>>(mutation);

  const handleDelete = async () => {
    try {
      await deleteItem({ variables: { input: { id } } });
      toastSuccess(successMessage);
      await onDeleted();
    } catch (e) {
      toastError(getGraphQLErrorMessage(e) ?? t('general.error.somethingWentWrong'));
    }
  };

  return (
    <Modal open={open} onClose={onClose} maxWidth="sm" title={dialogTitle}>
      <Typography variant="body1">{itemText}</Typography>

      <Alert severity="warning" sx={{ mt: 2 }}>
        {t('rules.delete.draftOnlyWarning')}
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button onClick={onClose}>{t('rules.delete.cancelButton')}</Button>
        <LoadingButton loading={deleting} variant="contained" color="error" onClick={handleDelete}>
          {t('rules.delete.confirmButton')}
        </LoadingButton>
      </Box>
    </Modal>
  );
};
